import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Images } from '../../utils/imageConstants';
import MembersTab from './components/MembersTab';
import TournamentsTab from './components/TournamentsTab';
import CreateTeamFlexModal from './components/CreateTeamFlexModal';
import TeamCreatedSuccessModal from './components/TeamCreatedSuccessModal';
import * as myTeamApi from './api/myTeamApi';
import type { IMyTeam, ITournament } from './types/myTeam';
import type { CreateTeamFlexFormData } from './components/CreateTeamFlexModal';

function MyTeamDetail() {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const fromStateInitial = (location.state as { team?: IMyTeam } | null)?.team;
  const [team, setTeam] = useState<IMyTeam | null | undefined>(() =>
    fromStateInitial?.teamId === teamId ? fromStateInitial : undefined
  );
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'members' | 'tournaments'>('members');
  const [membersSearchModalOpen, setMembersSearchModalOpen] = useState(false);
  const [tournamentsSearchModalOpen, setTournamentsSearchModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [selectedTournamentSnapshotId, setSelectedTournamentSnapshotId] = useState<string | null>(null);
  const [selectedTournamentName, setSelectedTournamentName] = useState<string | null>(null);

  const loadTeam = useCallback(
    async (forceRefresh = false) => {
      const fromState = (location.state as { team?: IMyTeam } | null)?.team;
      if (!forceRefresh && fromState?.teamId === teamId) {
        setTeam(fromState);
        return;
      }
      setLoading(true);
      try {
        const teams = await myTeamApi.fetchTeamsByTeamAdminId();
        const found = teams.find((t) => t.teamId === teamId) ?? null;
        setTeam(found);
      } catch (err) {
        console.error('Failed to load team', err);
        toast.error('Failed to load team details.');
        setTeam(null);
      } finally {
        setLoading(false);
      }
    },
    [teamId, location.state]
  );

  useEffect(() => {
    const fromState = (location.state as { team?: IMyTeam } | null)?.team;
    if (fromState?.teamId === teamId) {
      setTeam(fromState);
      return;
    }
    setTeam(undefined);
    loadTeam();
  }, [teamId, location.state, loadTeam]);

  const handleBack = () => {
    navigate('/my-team');
  };

  const handleEditClick = () => {
    setEditModalOpen(true);
  };

  const handleShareClick = () => {
    setShareModalOpen(true);
  };

  const handleTournamentClick = useCallback((tournament: ITournament) => {
    if (tournament.teamSnapshotId) {
      setSelectedTournamentSnapshotId(tournament.teamSnapshotId);
      setSelectedTournamentName(tournament.tournamentName ?? 'Tournament');
      setActiveTab('members');
    }
  }, []);

  const handleClearTournamentContext = useCallback(() => {
    setSelectedTournamentSnapshotId(null);
    setSelectedTournamentName(null);
  }, []);

  const handleEditSubmit = useCallback(
    async (data: CreateTeamFlexFormData) => {
      if (!team?.teamId) return;
      try {
        await myTeamApi.updateTeamByPlayer({
          teamId: team.teamId,
          teamName: data.teamName.trim(),
          teamCode: data.teamCode?.trim() || undefined,
          description: data.description?.trim() || undefined,
        });
        toast.success('Team updated successfully.');
        setEditModalOpen(false);
        await loadTeam(true);
      } catch (err) {
        console.error('Failed to update team', err);
        toast.error('Failed to update team. Please try again.');
      }
    },
    [team, loadTeam]
  );

  if (team === undefined || loading) {
    return (
      <div className="my-team-detail">
        <div className="myteam-detail-card">
          <div className="myteam-container myteam-container-body">
            <div className="myteam-loading">
              <span>Loading team...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="my-team-detail">
        <div className="myteam-detail-card">
          <div className="myteam-container myteam-container-body">
            <div className="myteam-empty-state">
              <p className="myteam-empty-message">We couldn&apos;t find that team.</p>
              <button type="button" className="myteam-empty-cta" onClick={handleBack}>
                Back to My Teams
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-team-detail">
      <div className="myteam-detail-card">
        <header className="myteam-detail-header">
          <div className="myteam-detail-header-left">
            <button
              type="button"
              className="myteam-detail-back"
              onClick={handleBack}
              aria-label="Back to My Teams"
            >
              <img src={Images.white_back_arrow} alt="" />
            </button>
            <div className="myteam-detail-header-info">
              <h1 className="myteam-detail-name">{team.teamName}</h1>
              <span className="myteam-detail-id">CricCenter Team ID: <span className="myteam-team-detail-id-value">{team.cricCenterTeamId}</span>
              </span>
            </div>
          </div>
          <div className="myteam-detail-header-right">
            <button type="button" className="myteam-detail-share" onClick={handleShareClick}>
              <img src={Images.share_icon} alt="" />
              Share
            </button>
            <button type="button" className="myteam-detail-edit" onClick={handleEditClick}>
              Edit
            </button>
          </div>
        </header>
        <div className="myteam-container myteam-container-body">
          <div className="myteam-detail-tabs-row">
            <div className="myteam-detail-tabs">
              <button
                type="button"
                className={`myteam-detail-tab ${activeTab === 'members' ? 'active' : ''}`}
                onClick={() => setActiveTab('members')}
              >
                Members
              </button>
              <button
                type="button"
                className={`myteam-detail-tab ${activeTab === 'tournaments' ? 'active' : ''}`}
                onClick={() => setActiveTab('tournaments')}
              >
                Tournaments
              </button>
            </div>
          </div>
          {activeTab === 'members' && (
            <MembersTab
              teamId={team.teamId}
              teamName={team.teamName}
              captainPlayerId={team.captainPlayerId}
              viceCaptainPlayerId={team.viceCaptainPlayerId}
              wkPlayerId={team.wkPlayerId}
              canRemovePlayerFromTeam={team.canRemovePlayerFromTeam}
              searchModalOpen={membersSearchModalOpen}
              onSearchModalOpenChange={setMembersSearchModalOpen}
              teamSnapshotId={selectedTournamentSnapshotId}
              tournamentContextName={selectedTournamentName}
              onClearTournamentContext={handleClearTournamentContext}
            />
          )}
          {activeTab === 'tournaments' && (
            <TournamentsTab
              teamId={team.teamId}
              teamName={team.teamName}
              team={team}
              searchModalOpen={tournamentsSearchModalOpen}
              onSearchModalOpenChange={setTournamentsSearchModalOpen}
              onTournamentClick={handleTournamentClick}
            />
          )}
        </div>
      </div>

      <CreateTeamFlexModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSubmit={handleEditSubmit}
        initialData={team}
      />

      <TeamCreatedSuccessModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        teamName={team.teamName}
        teamId={team.cricCenterTeamId ?? team.teamId}
        onDone={() => setShareModalOpen(false)}
        title="Share Team"
      />
    </div>
  );
}

export default MyTeamDetail;
